import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import { getDocuments, type ActorDocuments, type DocumentContract } from '../lib/api';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Career.css';

export interface CareerDocumentsProps {
  tenantId: string;
  onViewSource: (sourceId: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const CONTRACT_KIND_LABEL: Record<string, string> = {
  deal_memo: 'Deal memo',
  engagement: 'Engagement',
  nda: 'NDA',
  amendment: 'Amendment',
  release: 'Release',
  other: 'Contract',
};

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  received: 'Received',
  under_review: 'Under review',
  signed: 'Signed',
  countersigned: 'Countersigned',
  declined: 'Declined',
};

const UNION_STATUS_LABEL: Record<string, string> = {
  non_union: 'Non-union',
  eligible: 'Eligible',
  apprentice: 'Apprentice',
  member: 'Member · in good standing',
  financial_core: 'Financial core',
  other: 'Member',
};

// CAREER / DOCUMENTS — Contracts (status badges + optional original), the
// Tax-and-filings summary, and the Union card. A read surface (the only write
// on this backend is the profile upsert, owned by onboarding).
export function CareerDocuments({ tenantId, onViewSource }: CareerDocumentsProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<ActorDocuments | null>(null);
  const [loadFailure, setLoadFailure] = useState<ReadFailure | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getDocuments(tenantId);
        if (cancelled) return;
        setData(d);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setLoadFailure(describeReadFailure(err));
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, reloadNonce]);

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Documents" line="One moment…" />;
  }
  if (state === 'error' || !data) {
    return (
      <div class="career">
        <PlaceholderScreen
          eyebrow="Documents"
          line={loadFailure?.headline ?? "Couldn't load your documents."}
          soft={loadFailure?.detail}
        />
        {loadFailure?.retryable ? (
          <button
            type="button"
            class="career__add-toggle"
            onClick={() => {
              setState('loading');
              setReloadNonce((n) => n + 1);
            }}
          >
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  const { contracts, taxSummary, union } = data;
  const hasUnion = Boolean(union.name || union.status || union.memberId);

  return (
    <div class="career">
      <div class="career__eyebrow">Documents</div>

      <div class="career__grp">Contracts</div>
      {contracts.length === 0 ? (
        <div class="career__note" data-testid="contracts-empty">
          No contracts yet. Deal memos and releases land here once they&rsquo;re in.
        </div>
      ) : (
        contracts.map((c) => <ContractCard key={c.id} contract={c} onViewSource={onViewSource} />)
      )}

      <div class="career__grp">Tax and filings</div>
      <div class="career__card" style="cursor:default" data-testid="tax-summary">
        <div class="career__card-top">
          <span class="career__card-nm">Income summary · {taxSummary.year}</span>
        </div>
        <div class="career__card-sub">
          {taxSummary.creditCount} deductible {taxSummary.creditCount === 1 ? 'entry' : 'entries'} ·{' '}
          {taxSummary.expenseCount} total logged
        </div>
      </div>

      <div class="career__grp">Union</div>
      {hasUnion ? (
        <>
          <div class="career__kv" data-testid="union-row">
            <span class="career__kv-k">{union.name ?? 'Union'}</span>
            <span class="career__kv-v">
              {union.status ? (UNION_STATUS_LABEL[union.status] ?? union.status) : 'Status not set'}
            </span>
          </div>
          {union.memberId && (
            <div class="career__kv">
              <span class="career__kv-k">Member ID</span>
              <span class="career__kv-v">{union.memberId}</span>
            </div>
          )}
        </>
      ) : (
        <div class="career__note" data-testid="union-empty">
          No union on file — membership is captured at onboarding.
        </div>
      )}
    </div>
  );
}

function ContractCard({
  contract,
  onViewSource,
}: {
  contract: DocumentContract;
  onViewSource: (sourceId: string) => void;
}) {
  const kindLabel = (contract.kind && CONTRACT_KIND_LABEL[contract.kind]) ?? 'Contract';
  const status = contract.status ?? 'received';
  const statusLabel = CONTRACT_STATUS_LABEL[status] ?? status;

  return (
    <div class="career__card" style="cursor:default" data-testid="contract-card">
      <div class="career__card-top">
        <span class="career__card-nm">{kindLabel}</span>
        <span class={`career-badge career-badge--${status}`}>{statusLabel}</span>
      </div>
      {contract.rate && <div class="career__card-sub">{contract.rate}</div>}
      {contract.keyDates && <div class="career__card-sub">{contract.keyDates}</div>}
      {contract.respondBy && <div class="career__card-sub">Response due {contract.respondBy}</div>}
      {contract.sourceId && (
        <div class="career__src-chip">
          <button type="button" class="career__add-toggle" onClick={() => onViewSource(contract.sourceId!)}>
            View original
          </button>
        </div>
      )}
    </div>
  );
}
